import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TabbedPage from '../../../Elements/TabbedPage';
import OpportunitySecurityChecklistPanel from './OpportunitySecurityChecklistPanel';
import Loading from '../../../Elements/loading';
import ErrorMessage from '../../../Elements/ErrorMessage';
import OpportunitySecurityChecklistItem from '../../../Domain/OpportunitySecurityChecklistItem';
import OpportunitySecurityChecklistService from '../../../Services/opportunitySecurityChecklistService';
import OpportunitySecurityCompletionTypeEnum from '../../../Enums/OpportunitySecurityCompletionTypeEnum';
import GeneralEntryLogFigma from '../../../Elements/GeneralEntryLog/GeneralEntryLogFigma';
import OpportunityCompletionEntryLogDescription from './OpportunityCompletionEntryLogDescription';
import OpportunityRetentionDetailEntryLogDescription from './OpportunityRetentionDetailEntryLogDescription';

// Security Checklist tab content, split into sub-tabs via the shared TabbedPage:
//   - "Checklist"      — the read view + the Import from Contract action (OpportunitySecurityChecklistPanel).
//   - "Data Transfer"  — Section A completion entries.
//   - "Data Erasure"   — Section B data-erasure completion entries.
//   - "Data Retention" — Section B Retain Until date (in the panel title) + retention-detail log.
//
// The checklist is fetched ONCE here and shared with every sub-tab, since TabbedPage remounts the active tab on
// each switch (refreshTabOnChange). Each entry-log tab still fetches its own entry list, and holds exactly one
// GeneralEntryLogFigma so the popup-header add buttons never collide (see FRONTEND.md).
//
// ONE piece of state - the raw API resource - with the domain item DERIVED from it, because the sub-tabs read the
// item's fields while ShowEntityData converts internally. loadChecklist goes down as reloadChecklist so the
// Checklist panel can refresh every sub-tab after a save.
export default function OpportunitySecurityChecklistTab({
  opportunityId,
  clientId,
  statusCode,
  actualEndOn,
  scheduleEdLinkSent,
}) {
  const [rawChecklist, setRawChecklist] = useState(null);
  const [loading, setLoading] = useState(false);

  const checklist = useMemo(
    () => (rawChecklist ? new OpportunitySecurityChecklistItem(rawChecklist) : null),
    [rawChecklist]
  );

  // Returns what it stored, so a sub-tab can refresh every tab and hand the same resource back to its own reader
  // (the Checklist panel's ShowEntityData refresh). Null on failure - the error is already reported here.
  const loadChecklist = useCallback(async () => {
    if (!opportunityId) return null;
    setLoading(true);
    try {
      const result = await OpportunitySecurityChecklistService.GetById(opportunityId);
      if (result && result.error) {
        ErrorMessage.SetErrorMessage(result.error);
        setRawChecklist(null);
        return null;
      }
      setRawChecklist(result);
      return result;
    } catch (error) {
      ErrorMessage.SetErrorMessage(error.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  if (loading) {
    return <Loading loading={true} />;
  }

  if (!checklist) {
    return null;
  }

  const tabs = [
    {
      label: 'Checklist',
      id: 'checklist',
      icon: 'fa-solid fa-shield-halved',
      component: (
        <OpportunitySecurityChecklistPanel
          opportunityId={opportunityId}
          clientId={clientId}
          actualEndOn={actualEndOn}
          statusCode={statusCode}
          scheduleEdLinkSent={scheduleEdLinkSent}
          rawChecklist={rawChecklist}
          checklist={checklist}
          reloadChecklist={loadChecklist}
        />
      ),
    },
    {
      label: 'Data Transfer',
      id: 'datatransfer',
      icon: 'fa-solid fa-right-left',
      component: (
        <GeneralEntryLogFigma
          description={OpportunityCompletionEntryLogDescription}
          context={{
            opportunityId,
            statusCode,
            completionType: OpportunitySecurityCompletionTypeEnum.DataTransfer,
            title: 'Data Transfer',
            confirmLabel: 'I confirm the data transfer to the client has been completed.',
            checklistExists: !!checklist.OpportunitySecurityChecklistId,
          }}
        />
      ),
    },
    {
      label: 'Data Erasure',
      id: 'dataerasure',
      icon: 'fa-solid fa-eraser',
      component: (
        <GeneralEntryLogFigma
          description={OpportunityCompletionEntryLogDescription}
          context={{
            opportunityId,
            statusCode,
            completionType: OpportunitySecurityCompletionTypeEnum.DataErasure,
            title: 'Data Erasure',
            confirmLabel: 'I confirm the data erasure has been completed.',
            checklistExists: !!checklist.OpportunitySecurityChecklistId,
          }}
        />
      ),
    },
    {
      label: 'Data Retention',
      id: 'dataretention',
      icon: 'fa-solid fa-box-archive',
      component: (
        <GeneralEntryLogFigma
          description={OpportunityRetentionDetailEntryLogDescription}
          context={{
            opportunityId,
            statusCode,
            checklistExists: !!checklist.OpportunitySecurityChecklistId,
            retainUntilDisplay: checklist.DataRetainUntilDisplay,
            // EntityTypeEnum.OpportunitySecurityChecklist = 73 (no frontend EntityType enum exists; the
            // Opportunity type is used as a literal 12 elsewhere too). Retention details are Comments on this
            // entity type, keyed by the OpportunitySecurityChecklistId. The add flow posts the OpportunityId
            // (the server resolves it to the checklist id + mirrors a breadcrumb onto the Opportunity's
            // Comments tab); reads use the checklist id directly.
            entityTypeId: 73,
            opportunitySecurityChecklistId: checklist.OpportunitySecurityChecklistId,
          }}
        />
      ),
    },
  ];

  return (
    <TabbedPage
      tabs={tabs}
      className='border rounded'
    />
  );
}
